import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import CountProductListRow from "./CountProductListRow";
import inventoryObjects from "../../clients/inventoryObjects";

const tableHeadStyle = {
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 2,
  background: (theme) => theme.palette.terrain[300],
  height: "24px",
  border: "1px solid",
  borderColor: (theme) => theme.palette.terrain[400],
  borderTop: "none",
  lineHeight: 1.2,
  fontSize: 10,
  fontWeight: 600,
};

// Ordinal ranking for parCheck sorting.
// Backend treats Over like Good for display (per Count Report work), so we mirror that here.
// Anything unknown/failed/missing par falls into the "Not Set" bucket.
const PAR_STATUS_ORDINAL = {
  [inventoryObjects.parStatusTypes.unknown]: 0, // Not Set
  [inventoryObjects.parStatusTypes.under]: 1, // Under
  [inventoryObjects.parStatusTypes.good]: 2, // Good
  [inventoryObjects.parStatusTypes.over]: 2, // Over treated as Good
};

const getParCheckOrdinal = (record) => {
  if (
    record?.failedParCalculation ||
    record?.historicalParLow === null ||
    record?.historicalParLow === undefined
  ) {
    return PAR_STATUS_ORDINAL[inventoryObjects.parStatusTypes.unknown];
  }
  const ordinal = PAR_STATUS_ORDINAL[record?.finalParStatus];
  if (ordinal === undefined) {
    return PAR_STATUS_ORDINAL[inventoryObjects.parStatusTypes.unknown];
  }
  return ordinal;
};

// Each extractor returns either a comparable primitive or null.
// Null values always sort to the bottom regardless of direction.
const sortExtractors = {
  product: (record) => {
    const name = record?.historicalProductName || record?.productName;
    if (!name) return null;
    return name.toLowerCase();
  },
  value: (record) => {
    // failedValueCalculation rows always go to the bottom.
    if (record?.failedValueCalculation) return null;
    const amount = record?.finalRecordValue?.amount;
    if (amount === null || amount === undefined) return null;
    return amount;
  },
  count: (record) => {
    const q = record?.quantityFound;
    if (q === null || q === undefined) return null;
    return q;
  },
  par: (record) => {
    const p = record?.historicalParLow;
    if (p === null || p === undefined) return null;
    return p;
  },
  parCheck: (record) => getParCheckOrdinal(record),
  vendor: (record) => {
    const v = record?.defaultVendorName;
    if (!v) return null;
    return v.toLowerCase();
  },
};

const compareValues = (a, b, direction) => {
  // Null-always-bottom: regardless of direction, null sorts after non-null.
  const aNull = a === null || a === undefined;
  const bNull = b === null || b === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;

  let cmp;
  if (typeof a === "string" && typeof b === "string") {
    cmp = a.localeCompare(b);
  } else {
    cmp = a < b ? -1 : a > b ? 1 : 0;
  }
  return direction === "desc" ? -cmp : cmp;
};

const CountProductListTable = ({
  records,
  openRecordCostSetModal,
  toggleParsModalOpen,
  openEditQuantityModal,
  hidePar = false,
}) => {
  const [sort, setSort] = useState({ key: null, direction: "asc" });

  const handleSort = (columnKey) => {
    setSort((prev) => {
      if (prev.key === columnKey) {
        return {
          key: columnKey,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { key: columnKey, direction: "asc" };
    });
  };

  const sortedRecords = useMemo(() => {
    if (!records || !sort.key) return records || [];
    const extractor = sortExtractors[sort.key];
    if (!extractor) return records;
    // Copy before sorting to avoid mutating the prop.
    const copy = [...records];
    copy.sort((a, b) =>
      compareValues(extractor(a), extractor(b), sort.direction)
    );
    return copy;
  }, [records, sort]);

  const renderSortLabel = (columnKey, label) => (
    <TableSortLabel
      active={sort.key === columnKey}
      direction={sort.key === columnKey ? sort.direction : "asc"}
      onClick={() => handleSort(columnKey)}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <TableContainer
      sx={{
        overflow: "scroll",
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        border: "1px solid",
        borderBottom: "none",
        borderColor: (theme) => theme.palette.terrain[400],
      }}
    >
      <Table sx={{ width: "100%" }} aria-label="customized table">
        <TableHead sx={{ height: "24px" }}>
          <TableRow sx={{ height: 24 }}>
            <TableCell
              sx={[tableHeadStyle, { width: "376px", borderLeft: "none" }]}
            >
              {renderSortLabel("product", "Product Name")}
            </TableCell>
            <TableCell sx={[tableHeadStyle, { width: "100px" }]}>
              {renderSortLabel("value", "Value on Hands")}
            </TableCell>
            <TableCell sx={[tableHeadStyle, { width: "100px" }]}>
              {renderSortLabel("count", "Count Total")}
            </TableCell>
            {!hidePar ? (
              <>
                <TableCell sx={[tableHeadStyle, { width: "100px" }]}>
                  {renderSortLabel("par", "Par")}
                </TableCell>
                <TableCell
                  sx={[tableHeadStyle, { width: "100px", textAlign: "center" }]}
                >
                  {renderSortLabel("parCheck", "Par Check")}
                </TableCell>
              </>
            ) : null}
            <TableCell sx={[tableHeadStyle, { width: "140px" }]}>
              {renderSortLabel("vendor", "Vendor")}
            </TableCell>
            <TableCell
              sx={[tableHeadStyle, { width: "60px", textAlign: "center" }]}
            >
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedRecords.map((x) => (
            <CountProductListRow
              key={x.id || x.productName}
              record={x}
              openRecordCostSetModal={openRecordCostSetModal}
              toggleParsModalOpen={toggleParsModalOpen}
              openEditQuantityModal={openEditQuantityModal}
              hidePar={hidePar}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CountProductListTable;
